import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';

import avatar from '../../assets/images/logos/avatar.png';
import messageIcon from '../../assets/images/pre/message.svg';
import boxIcon from '../../assets/images/pre/box.svg';
import groupIcon from '../../assets/images/pre/group.svg';
import frameIcon from '../../assets/images/pre/frame.svg';
import podsIcon from '../../assets/images/pre/pods.svg';
import settingIcon from '../../assets/images/pre/setting.svg';
import verifyIcon from '../../assets/images/pre/verify.svg';

interface BaoDesktopMenuDropdownProps {
  user: any;
  setIsDesktopMenuOpen: (isOpen: boolean) => void;
  highlightChats: boolean;
  handleLogout: () => Promise<void>;
}

const BaoDesktopMenuDropdown: React.FC<BaoDesktopMenuDropdownProps> = ({
  user,
  setIsDesktopMenuOpen,
  highlightChats,
  handleLogout,
}) => {
  return (
    <div
      className="fixed right-8 top-0 w-64 bg-white rounded-2xl shadow-lg border border-gray-200 py-3 z-50 max-h-screen overflow-y-auto custom-scrollbar desktop-menu-dropdown"
      style={{
        scrollbarWidth: "thin",
        scrollbarColor: "white #f3f4f6",
      }}
    >
      {/* Profile Section */}
      <div className="flex ml-6">
        <div className="flex items-center gap-6">
          <Link
            to="/account"
            className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full hover:ring-1 hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            title="Profile"
            aria-label="Go to profile page"
          >
            <img
              src={user?.profileImage || avatar}
              alt="User Icon"
              className="w-10 h-10 rounded-full object-cover"
              width="32"
              height="32"
            />
          </Link>
          <div className="flex-col">
            <p className="text-xs text-gray-500">My profile</p>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.firstName
                  ? user.firstName
                  : user?.lastName
                    ? user.lastName
                    : user?.email
                      ? user.email.split("@")[0]
                      : "User"}
              {user?.isVerifiedSeller && (
                <img src={verifyIcon} alt="Verified" className="w-3.5 h-3.5 inline-block shrink-0" />
              )}
            </h3>
          </div>
        </div>
      </div>

      {/* Create a new listing button */}
      <div className="px-3 py-3">
        <Link
          to="/create-listing"
          className="block w-full px-3 py-2 rounded-lg font-medium text-xs transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            backgroundColor: "#E3F2FD",
            color: "#64B5F6",
          }}
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center justify-center space-x-1.5">
            <span>Create a new listing</span>
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: "#64B5F6" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
        </Link>
      </div>

      {/* Navigation Menu Items */}
      <div className="space-y-0.5 px-2">
        {/* Chats */}
        <Link
          to="/messages"
          className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg ${highlightChats ? "bg-gray-50" : ""
            }`}
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={messageIcon}
              alt="Message"
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div
                className="font-medium text-sm"
                style={{ color: "#6A6A6A" }}
              >
                Chats
              </div>
            </div>
          </div>
        </Link>

        {/* My listings */}
        <Link
          to="/my-listings"
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={boxIcon}
              alt="Box"
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div className="font-medium text-sm" style={{ color: "#6A6A6A" }}>
                My listings
              </div>
            </div>
          </div>
        </Link>

        {/* My requests */}
        <Link
          to="/my-requests"
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={groupIcon}
              alt="Group"
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div
                className="font-medium text-sm"
                style={{ color: "#6A6A6A" }}
              >
                My requests
              </div>
            </div>
          </div>
        </Link>

        <Link
          to="/bookmarks"
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={frameIcon}
              alt=""
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div
                className="font-medium text-sm"
                style={{ color: "#6A6A6A" }}
              >
                Bookmarks
              </div>
            </div>
          </div>
        </Link>

        {/* Help Centre */}
        <Link
          to="/help-center"
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={podsIcon}
              alt="Pods"
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div
                className="font-medium text-sm"
                style={{ color: "#6A6A6A" }}
              >
                Help Centre
              </div>
            </div>
          </div>
        </Link>

        {/* Settings */}
        <Link
          to="/settings"
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
          onClick={() => setIsDesktopMenuOpen(false)}
        >
          <div className="flex items-center space-x-2">
            <img
              src={settingIcon}
              alt="Setting"
              className="w-4 h-4"
              style={{ color: "#64B5F6" }}
            />
            <div>
              <div
                className="font-medium text-sm"
                style={{ color: "#6A6A6A" }}
              >
                Settings
              </div>
              <div className="text-xs text-gray-500">
                Set your account preferences
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Logout Button */}
      <div className="px-3 pt-3 border-t border-gray-100">
        <Button
          type="button"
          unstyled
          onClick={async () => {
            await handleLogout();
          }}
          className="w-full bg-gray-100 px-3 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#6A6A6A' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <div className="text-left">
              <div className="font-medium text-xs" style={{ color: '#6A6A6A' }}>Log Out</div>
              <div className="text-xs" style={{ color: '#6A6A6A' }}>Log out of BAO Afrik</div>
            </div>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default BaoDesktopMenuDropdown;
